// goalAdherence.js — how a goal's HISTORY feeds its pillar score (2026-10-17).
//
// Before this, a cap goal was scored on today alone ("4h of 7h limit today"
// read full marks every morning after five crossings in a week) and a count
// goal on this week's ratio. Now:
//   screen_time (daily cap)   share of tracked days under the cap, trailing
//                             14 days; over/under only — partial and no-data
//                             days are excluded, never counted as under; today
//                             counts only once already over.
//   plaid_spend (monthly cap) half the share of the last two COMPLETE months
//                             within the cap (months with bank data), half this
//                             month's pace word (ahead 1, on_track .8, behind
//                             .4, at_risk 0).
//   workout (weekly count)    half this week's pace word (hit 1, on_track .85,
//                             behind .5, at_risk .2), half last week's ratio.
// HISTORY ONLY COUNTS PERIODS ON OR AFTER THE GOAL'S created_at: a day, a
// month or a week counts only if it STARTS on or after the goal's local
// creation date. Pure ESM; the client (Scorify.jsx financeData) and the
// server (api/lib/pillars-server.js) call the same functions.
import { screenTimeDayStatus } from './screenTimeUsage.js';
import { txMatchesSpendGoal, txLocalDay } from './spend.js';
import { localWeekStart, localDateInZone } from './weekWindow.js';
import { weeklyCountStatus, monthlyCapStatus, monthPositionOf, COUNT_STATUS_POINTS, CAP_STATUS_POINTS } from './goalPace.js';
import { isWorkoutCountGoal, workoutGoalTarget } from './goalLink.js';
import { countWorkoutSessions, gymHistoryOf } from './workoutSessions.js';

export const ADHERENCE_WINDOW_DAYS = 14;
export const ADHERENCE_MIN_DAYS = 3;
export const ADHERENCE_MONTHS = 2;

const dayOf = (v) => (typeof v === 'string' ? v.slice(0, 10) : null);
export function addDays(ymd, n) { const d = new Date(`${String(ymd).slice(0, 10)}T12:00:00Z`); if (isNaN(d.getTime())) return null; d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); }
const monthBack = (month, back) => { const [y, m] = month.split('-').map(Number); const idx = (y * 12 + (m - 1)) - back; return `${Math.floor(idx / 12)}-${String((idx % 12) + 1).padStart(2, '0')}`; };
const round1 = (n) => Math.round(n * 10) / 10;

/** The goal's creation date as a local 'YYYY-MM-DD' (zone optional → device local). */
export function goalCreatedLocalDate(goal, zone) {
  const iso = goal?.created_at;
  if (!iso) return null;
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return null;
  return zone ? localDateInZone(ms, zone) : (() => { const d = new Date(ms); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })();
}

/** Daily cap (screen_time): share of tracked days under the cap. */
export function screenCapAdherence({ goal, screenTimeDays = [], today, zone, nowMs = Date.now(), windowDays = ADHERENCE_WINDOW_DAYS } = {}) {
  if (!today) return null;
  const created = goalCreatedLocalDate(goal, zone);
  const byDate = new Map((screenTimeDays || []).map(r => [dayOf(String(r?.date || '')), r]));
  let under = 0, over = 0;
  for (let i = windowDays; i >= 1; i--) {
    const d = addDays(today, -i);
    if (created && d < created) continue;
    const st = screenTimeDayStatus(byDate.get(d) || null, zone, nowMs);
    if (st === 'under') under++; else if (st === 'over') over++;
  }
  // Today counts only once it is already over (an open day cannot be "under").
  const todayRow = byDate.get(today);
  if (todayRow && (!created || today >= created) && screenTimeDayStatus(todayRow, zone, nowMs) === 'over') over++;
  const tracked = under + over;
  if (tracked < ADHERENCE_MIN_DAYS) return { kind: 'cap_day', hasData: false, score01: 0, tracked, under, over, detail: tracked === 0 ? 'no tracked days yet' : `only ${tracked} tracked day${tracked === 1 ? '' : 's'} so far` };
  const score01 = under / tracked;
  return { kind: 'cap_day', hasData: true, score01, tracked, under, over, detail: `under the limit ${under} of ${tracked} tracked days, last ${windowDays}` };
}

/** Monthly cap (plaid_spend): past complete months within the cap + this month's pace. */
export function spendCapAdherence({ goal, transactions = [], today, zone, months = ADHERENCE_MONTHS } = {}) {
  if (!today) return null;
  const target = Number(goal?.period_target ?? goal?.target_value) || 0;
  if (!(target > 0)) return null;
  const txs = Array.isArray(transactions) ? transactions : [];
  if (!txs.length) return { kind: 'cap_month', hasData: false, score01: 0, detail: 'connect a bank to score this' };
  const cfg = goal?.source_config || {};
  const created = goalCreatedLocalDate(goal, zone);
  const pos = monthPositionOf(today);
  const thisMonth = pos.month;
  const spentIn = (month, upToDay = null) => txs.filter(t => { const d = txLocalDay(t, zone); return d && d.startsWith(month) && (!upToDay || d <= upToDay) && txMatchesSpendGoal(t, cfg); }).reduce((a, t) => a + Number(t.amount || 0), 0);
  const past = [];
  for (let back = 1; back <= months; back++) {
    const m = monthBack(thisMonth, back);
    if (created && `${m}-01` < created) continue;                    // period started before the goal existed
    if (!txs.some(t => String(txLocalDay(t, zone) || '').startsWith(m))) continue; // no bank data for that month
    const spent = spentIn(m);
    past.push({ month: m, spent, within: spent <= target });
  }
  const current = spentIn(thisMonth);
  const status = monthlyCapStatus({ current, target, fractionElapsed: pos.fractionElapsed });
  const pacePts = CAP_STATUS_POINTS[status] ?? 0;
  const pastShare = past.length ? past.filter(p => p.within).length / past.length : null;
  const score01 = pastShare == null ? pacePts : 0.5 * pastShare + 0.5 * pacePts;
  const pastDetail = past.length ? `${past.filter(p => p.within).length}/${past.length} past month${past.length === 1 ? '' : 's'} within the cap` : 'no complete month yet';
  return { kind: 'cap_month', hasData: true, score01, status, current, target, past, detail: `${pastDetail}; this month ${status === 'at_risk' && current >= target ? 'over' : status.replace('_', ' ')} ($${Math.round(current)} of $${Math.round(target)})` };
}

/**
 * Workout SESSIONS in [start, end] (src/lib/workoutSessions.js, 2026-10-27):
 * workouts rows with a HealthKit/manual twin collapsed, plus one per gym-habit
 * day with no row. Used to be distinct DAYS, so two sessions on one day read 1.
 */
export function workoutSessionsBetween({ workouts = [], gymHistory = [], start, end }) {
  return countWorkoutSessions({ workouts, gymHistory, start, end }).count;
}

/** Weekly count (workouts): this week's pace word + last week's ratio. */
export function weeklyCountAdherence({ goal, habits = [], workouts = [], gymHistory = null, today, zone } = {}) {
  if (!today) return null;
  const target = workoutGoalTarget(goal, habits);
  if (!(target > 0)) return null;
  const gym = (habits || []).find(h => h && (h.id === 'gym' || h.name === 'gym'));
  let hist = gymHistory;
  if (hist == null && gym) hist = gymHistoryOf(gym);
  const created = goalCreatedLocalDate(goal, zone);
  const weekStart = localWeekStart(today);
  const thisWeek = workoutSessionsBetween({ workouts, gymHistory: hist || [], start: weekStart, end: today });
  const status = weeklyCountStatus({ current: thisWeek, target, today });
  const statusPts = COUNT_STATUS_POINTS[status] ?? 0;
  const lastStart = addDays(weekStart, -7), lastEnd = addDays(weekStart, -1);
  const lastCounts = !created || lastStart >= created;
  const lastWeek = lastCounts ? workoutSessionsBetween({ workouts, gymHistory: hist || [], start: lastStart, end: lastEnd }) : null;
  const lastRatio = lastWeek == null ? null : Math.min(1, lastWeek / target);
  const score01 = lastRatio == null ? statusPts : 0.5 * statusPts + 0.5 * lastRatio;
  return { kind: 'count_week', hasData: true, score01, status, thisWeek, lastWeek, target, detail: `${thisWeek} of ${target} this week (${status.replace('_', ' ')})${lastWeek != null ? `, ${lastWeek} of ${target} last week` : ''}` };
}

/**
 * Dispatcher: the adherence record for one goal, or null when the goal has
 * no history-based rule (manual targets keep the ratio scoring).
 * ctx: { today, zone?, nowMs?, screenTimeDays, transactions, workouts, habits }
 */
export function goalAdherence(goal, ctx = {}) {
  if (!goal || goal.completed) return null;
  if (goal.source_type === 'screen_time') return screenCapAdherence({ goal, ...ctx });
  if (goal.source_type === 'plaid_spend' && (goal.period || 'month') === 'month') return spendCapAdherence({ goal, ...ctx });
  if (isWorkoutCountGoal(goal)) return weeklyCountAdherence({ goal, ...ctx });
  return null;
}

/** The Money pillar's Budget Adherence input: mean over the user's spend caps. */
export function budgetAdherence(goals, ctx = {}) {
  const caps = (goals || []).filter(g => g && !g.completed && g.source_type === 'plaid_spend' && (g.period || 'month') === 'month');
  if (!caps.length) return null;
  const recs = caps.map(g => spendCapAdherence({ goal: g, ...ctx })).filter(Boolean);
  if (!recs.length) return null;
  if (recs.every(r => !r.hasData)) return { hasData: false, score01: 0, detail: recs[0].detail };
  const live = recs.filter(r => r.hasData);
  const score01 = live.reduce((a, r) => a + r.score01, 0) / live.length;
  return { hasData: true, score01: round1(score01 * 100) / 100, detail: live.length === 1 ? live[0].detail : `${live.length} caps: ${live.map(r => `${Math.round(r.score01 * 100)}%`).join(', ')}` };
}
